import React, { useState, useEffect, useMemo } from "react";
import axiosClient from "../utils/axiosClient";

const OnboardingMonitor = () => {
  // --- Data State ---
  const [originalData, setOriginalData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // --- Dynamic Filters & Sorting State ---
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "signup_date",
    direction: "desc",
  });

  // --- Pagination State ---
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // 1. Fetch Data
  useEffect(() => {
    const fetchOnboardingData = async () => {
      try {
        setLoading(true);
        const response = await axiosClient.get(
          "/super-admin/onboarding-monitor",
        );
        setOriginalData(response.data);
        setLoading(false);
      } catch (err) {
        console.error("Failed to fetch onboarding data", err);
        setError("Failed to load data. Please check the console.");
        setLoading(false);
      }
    };
    fetchOnboardingData();
  }, []);

  // 2. Dynamic Search & Sort Logic (useMemo ensures this only runs when data or filters change)
  const processedData = useMemo(() => {
    let sortableItems = [...originalData];

    // --- A. Apply Search Filter ---
    if (searchTerm) {
      const lowercasedFilter = searchTerm.toLowerCase();
      sortableItems = sortableItems.filter((item) => {
        return (
          (item.first_name &&
            item.first_name.toLowerCase().includes(lowercasedFilter)) ||
          (item.last_name &&
            item.last_name.toLowerCase().includes(lowercasedFilter)) ||
          (item.email && item.email.toLowerCase().includes(lowercasedFilter)) ||
          (item.organization_name &&
            item.organization_name.toLowerCase().includes(lowercasedFilter))
        );
      });
    }

    // --- B. Apply Sorting ---
    if (sortConfig !== null) {
      sortableItems.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Handle nulls/undefined gracefully
        if (aValue === null || aValue === undefined) aValue = "";
        if (bValue === null || bValue === undefined) bValue = "";

        if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }

    return sortableItems;
  }, [originalData, searchTerm, sortConfig]);

  // 3. Pagination Calculations (Based on the processed data)
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = Math.max(0, indexOfLastItem - itemsPerPage);
  const currentItems = processedData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(processedData.length / itemsPerPage);

  // --- Event Handlers ---
  const handleSort = (key) => {
    let direction = "asc";
    if (
      sortConfig &&
      sortConfig.key === key &&
      sortConfig.direction === "asc"
    ) {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to page 1 when searching
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // --- Helper to render Sort Icons ---
  const getSortIcon = (columnName) => {
    if (sortConfig.key !== columnName)
      return <span className="text-gray-300 ml-1">↕</span>;
    return sortConfig.direction === "asc" ? (
      <span className="text-sky-600 ml-1">↑</span>
    ) : (
      <span className="text-sky-600 ml-1">↓</span>
    );
  };

  if (loading)
    return (
      <div className="p-6 text-center text-gray-500">
        Loading dynamic monitor data...
      </div>
    );
  if (error) return <div className="p-6 text-center text-red-500">{error}</div>;

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <h2 className="text-3xl font-bold text-gray-800 mb-6">
        Dynamic Onboarding Monitor
      </h2>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        {/* --- Top Controls: Search & Rows --- */}
        <div className="p-4 border-b flex flex-col md:flex-row justify-between items-center bg-gray-50 gap-4">
          {/* Rows Per Page */}
          <div className="flex items-center text-sm text-gray-600">
            <label htmlFor="itemsPerPage" className="mr-2 font-medium">
              Show
            </label>
            <select
              id="itemsPerPage"
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
              className="border border-gray-300 rounded p-1.5 text-gray-700 focus:outline-none focus:ring-2 focus:ring-sky-500 cursor-pointer bg-white"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
            <span className="ml-2 font-medium">entries</span>
          </div>

          {/* Global Search Bar */}
          <div className="relative w-full md:w-72">
            <input
              type="text"
              placeholder="Search Name, Email, or Org..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="w-full border border-gray-300 rounded-lg pl-4 pr-10 py-2 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent transition"
            />
            {searchTerm && (
              <button
                onClick={() => {
                  setSearchTerm("");
                  setCurrentPage(1);
                }}
                className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* --- Data Table --- */}
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm whitespace-nowrap">
            <thead className="bg-gray-100 uppercase text-gray-600 font-semibold border-b select-none">
              <tr>
                <th
                  className="p-4 cursor-pointer hover:bg-gray-200 transition"
                  onClick={() => handleSort("first_name")}
                >
                  Admin / Org {getSortIcon("first_name")}
                </th>
                <th
                  className="p-4 cursor-pointer hover:bg-gray-200 transition"
                  onClick={() => handleSort("signup_date")}
                >
                  Signup Date {getSortIcon("signup_date")}
                </th>
                <th
                  className="p-4 cursor-pointer hover:bg-gray-200 transition text-center"
                  onClick={() => handleSort("day_1_sent")}
                >
                  Day 0 {getSortIcon("day_1_sent")}
                </th>
                <th
                  className="p-4 cursor-pointer hover:bg-gray-200 transition text-center"
                  onClick={() => handleSort("day_2_sent")}
                >
                  Day 2 {getSortIcon("day_2_sent")}
                </th>
                <th
                  className="p-4 cursor-pointer hover:bg-gray-200 transition text-center"
                  onClick={() => handleSort("day_3_sent")}
                >
                  Day 5 {getSortIcon("day_3_sent")}
                </th>
                <th
                  className="p-4 cursor-pointer hover:bg-gray-200 transition text-center"
                  onClick={() => handleSort("day_4_sent")}
                >
                  Day 12 {getSortIcon("day_4_sent")}
                </th>
                <th className="p-4 cursor-pointer hover:bg-gray-200 transition text-center">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {currentItems.length > 0 ? (
                currentItems.map((row) => (
                  <tr
                    key={row.user_id}
                    className="hover:bg-gray-50 transition duration-150 ease-in-out"
                  >
                    <td className="p-4">
                      <div className="font-bold text-gray-800">
                        {row.first_name} {row.last_name}
                      </div>
                      <div className="text-gray-500 text-xs">{row.email}</div>
                      <div className="text-sky-600 text-xs mt-1">
                        {row.organization_name?.slice(0, 30)}
                        {row.organization_name?.length > 30 && "..."}
                      </div>
                    </td>
                    <td className="p-4 text-gray-600 font-medium">
                      {new Date(row.signup_date).toLocaleDateString()}
                    </td>
                    <td className="p-4 text-center">
                      {row.day_1_sent ? (
                        <span
                          className="text-green-600 font-bold"
                          title={new Date(row.day_1_sent).toLocaleString()}
                        >
                          ✅
                        </span>
                      ) : (
                        <span className="text-gray-400 font-medium">-</span>
                      )}
                    </td>
                    <td className="p-4 text-center">
                      {row.day_2_sent ? (
                        <span
                          className="text-green-600 font-bold"
                          title={new Date(row.day_2_sent).toLocaleString()}
                        >
                          ✅
                        </span>
                      ) : (
                        <span className="text-gray-400 font-medium">-</span>
                      )}
                    </td>
                    <td className="p-4 text-center">
                      {row.day_3_sent ? (
                        <span
                          className="text-green-600 font-bold"
                          title={new Date(row.day_3_sent).toLocaleString()}
                        >
                          ✅
                        </span>
                      ) : (
                        <span className="text-gray-400 font-medium">-</span>
                      )}
                    </td>
                    <td className="p-4 text-center">
                      {row.day_4_sent ? (
                        <span
                          className="text-green-600 font-bold"
                          title={new Date(row.day_4_sent).toLocaleString()}
                        >
                          ✅
                        </span>
                      ) : (
                        <span className="text-gray-400 font-medium">-</span>
                      )}
                    </td>
                    <td className="p-4 text-center">
                      {row.report_count > 0 ? (
                        <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide">
                          Complete
                        </span>
                      ) : row.case_count > 0 ? (
                        <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide">
                          In Progress
                        </span>
                      ) : (
                        <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide">
                          Stalled
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="8"
                    className="p-8 text-center text-gray-500 font-medium bg-gray-50"
                  >
                    No results found matching "{searchTerm}"
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* --- Bottom Footer: Range & Pagination --- */}
        <div className="p-4 border-t flex flex-col md:flex-row justify-between items-center bg-gray-50 gap-4">
          <div className="text-sm text-gray-600 font-medium">
            Showing {processedData.length === 0 ? 0 : indexOfFirstItem + 1} to{" "}
            {Math.min(indexOfLastItem, processedData.length)} of{" "}
            {processedData.length} entries
          </div>

          {totalPages > 1 && (
            <nav className="flex items-center space-x-2">
              <button
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
                className={`px-3 py-1.5 rounded-md text-sm font-semibold transition duration-200 ${
                  currentPage === 1
                    ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                    : "bg-white text-sky-600 border border-gray-300 hover:bg-sky-50 hover:text-sky-700 shadow-sm"
                }`}
              >
                Prev
              </button>

              <div className="hidden sm:flex space-x-1">
                {[...Array(totalPages)].map((_, index) => (
                  <button
                    key={index + 1}
                    onClick={() => paginate(index + 1)}
                    className={`w-8 h-8 flex items-center justify-center rounded-md text-sm font-semibold transition duration-200 shadow-sm ${
                      currentPage === index + 1
                        ? "bg-sky-600 text-white ring-2 ring-sky-600 ring-offset-1"
                        : "bg-white text-gray-700 border border-gray-300 hover:bg-sky-50"
                    }`}
                  >
                    {index + 1}
                  </button>
                ))}
              </div>

              <button
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`px-3 py-1.5 rounded-md text-sm font-semibold transition duration-200 ${
                  currentPage === totalPages
                    ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                    : "bg-white text-sky-600 border border-gray-300 hover:bg-sky-50 hover:text-sky-700 shadow-sm"
                }`}
              >
                Next
              </button>
            </nav>
          )}
        </div>
      </div>
    </div>
  );
};

export default OnboardingMonitor;
